import { ArrowRight, ChevronDown, Pin, Plus, Power, RotateCcw, Search, ShieldCheck } from "lucide-react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { CompanyOnboarding } from "@/features/companies/CompanyOnboarding";
import { CompanyEditModal } from "@/features/companies/CompanyEditModal";
import { DashboardSummaryCards } from "@/features/dashboard/DashboardSummaryCards";
import { HomeBackdrop } from "@/features/companies/HomeBackdrop";
import { HomeHero } from "@/features/companies/HomeHero";
import { useSummaryDays } from "@/shared/components/SummaryPeriodPicker";
import {
  apiClient,
  type CompanyContext,
  type CompanyGroupRef,
  type CreatedCompany,
  type MentionSummary,
  resolveApiAssetUrl,
  type UserProfile
} from "@/shared/api/api-client";
import { ConfirmDialog } from "@/shared/components/ConfirmDialog";
import { Toast } from "@/shared/components/Toast";
import { errorMessage } from "@/shared/api/http";
import { activeCompanies } from "@/shared/utils/companies";
import { toMention, type Mention } from "@/shared/types/mention";
import { isMaster, isAdministrator } from "@/shared/utils/roles";
import { AppLink } from "@/shared/components/AppLink";
import { Select } from "@/shared/components/Select";

/** Quantas menções o feed do hero busca: 12 completa as linhas de 1, 2 e 3
 * colunas, então o laço emenda sem pulo em qualquer largura. */
const HERO_FEED_SIZE = 12;

/** O valor do filtro "Núcleo" pra empresa que não está em núcleo nenhum
 * (ou em nenhum que a pessoa enxergue). Não colide com id de núcleo, que é
 * número. */
const NO_GROUP = "__none__";

/** Filtro "Núcleo": `all` mostra tudo, `NO_GROUP` só as sem núcleo, e o id
 * de um núcleo só as empresas dele. Puro, pra o teste não precisar da tela. */
export function matchesGroupFilter(company: CompanyContext, groupFilter: string): boolean {
  if (groupFilter === "all") return true;
  const groups = company.groups ?? [];
  if (groupFilter === NO_GROUP) return groups.length === 0;
  return groups.some(group => group.id === groupFilter);
}

/** As opções do filtro: cada núcleo que aparece em alguma empresa, uma vez,
 * em ordem alfabética. */
export function groupFilterOptions(companies: CompanyContext[]): CompanyGroupRef[] {
  const byId = new Map<string, CompanyGroupRef>();
  for (const company of companies) {
    for (const group of company.groups ?? []) byId.set(group.id, group);
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
}

/** A empresa fixada no painel da Home; ausente = automático (última aberta). */
const HOME_COMPANY_STORAGE_KEY = "reputation_home_company";

function readStoredIds(key: string): string[] {
  try {
    const stored = window.localStorage.getItem(key);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

type CompanyHubProps = {
  profile: UserProfile;
  token: string;
  onCompanyCreated: (company: CreatedCompany) => Promise<void> | void;
  /** Chamada depois de desativar/reativar uma empresa: quem a recebe
   * relê o `/me`, que é a fonte do estado de ativação. */
  onCompanyStateChanged?: () => Promise<void> | void;
  /** Destino do atalho "Ir para Administração". Link, e não callback
   * (2026-10-26): vale "abrir em nova aba". */
  adminHref: string;
  isRecentOnly?: boolean;
};

/** Na Home, o título "Empresas recentemente acessadas" e os cartões vivem num
 * painel sólido só; na página de Empresas, nada embrulha nada. */
function HomePanel({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  return enabled ? <div className="home-companies-panel">{children}</div> : <>{children}</>;
}

export function CompanyHub({
  profile,
  token,
  onCompanyCreated,
  onCompanyStateChanged,
  adminHref,
  isRecentOnly = false
}: CompanyHubProps) {
  const [query, setQuery] = useState("");
  // Abre em ATIVAS (pedido da dona): a empresa desativada está fora de
  // circulação e não deve disputar espaço com as que se usa. "Todas" e
  // "Inativas" continuam a um clique — é por lá que o Máster a
  // reativa.
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive" | "admin">("active");
  const [sortBy, setSortBy] = useState<"name" | "recent">("name");
  // Filtro por núcleo (2026-11-03): só Máster e Administrador o veem — são
  // os cargos que enxergam empresas de mais de um time. `all` = todos.
  const [groupFilter, setGroupFilter] = useState<string>("all");
  const groupOptions = useMemo(() => groupFilterOptions(profile.companies), [profile.companies]);
  const canFilterByGroup = isAdministrator(profile) && groupOptions.length > 0;
  // Núcleo escolhido que sumiu (a lista foi relida e ele saiu): volta pra
  // "Todos" em vez de mostrar uma lista vazia sem opção marcada.
  const effectiveGroupFilter =
    !canFilterByGroup ||
    (groupFilter !== "all" && groupFilter !== NO_GROUP && !groupOptions.some(group => group.id === groupFilter))
      ? "all"
      : groupFilter;
  // "Criar empresa" fica na URL (`?nova=1`, 2026-10-16) pra a barra do topo
  // mostrar "Nova empresa" com a seta de voltar, como as outras telas.
  const [searchParams, setSearchParams] = useSearchParams();
  const creating = searchParams.get("nova") === "1";
  const setCreating = (open: boolean) =>
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        if (open) next.set("nova", "1");
        else next.delete("nova");
        return next;
      },
      { replace: !open }
    );
  const [editingCompany, setEditingCompany] = useState<CompanyContext | null>(null);
  const [visibleCount, setVisibleCount] = useState(21);
  const [dayLabel, setDayLabel] = useState("");
  const [summary, setSummary] = useState<MentionSummary | null>(null);
  // O período dos números (1, 7, 15 ou 30 dias até o último dado), o
  // mesmo da Visão geral — uma escolha só, lembrada no navegador.
  const [summaryDays, setSummaryDays] = useSummaryDays();
  const [recentMentions, setRecentMentions] = useState<Mention[]>([]);
  // Desativar/reativar: a empresa à espera do "sim" do diálogo, e o
  // desfecho da chamada.
  const [toggling, setToggling] = useState<CompanyContext | null>(null);
  const [toggleBusy, setToggleBusy] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);
  // O aviso que a casca manda ao nos trazer de volta (403 de empresa
  // desativada) vai no `state` da navegação — estado de rota, não
  // texto de erro lido por aqui.
  const location = useLocation();
  const [notice, setNotice] = useState<string | null>(
    () => (location.state as { notice?: string } | null)?.notice ?? null
  );
  const canToggleCompanies = isMaster(profile);

  useEffect(() => {
    const weekday = new Date().toLocaleDateString("pt-BR", { weekday: "long" });
    setDayLabel(weekday.charAt(0).toUpperCase() + weekday.slice(1));
  }, []);

  /**
   * Lidos no INICIALIZADOR, não num efeito. Dois motivos:
   *
   * 1. o resumo depende de "qual foi a última empresa visitada", e com
   *    efeito a primeira renderização sempre via `[]` — impossível
   *    distinguir "ainda não li" de "não há recente", que é justamente a
   *    condição que decide se a seção aparece;
   * 2. a ordenação por recência piscava na ordem errada no primeiro
   *    paint.
   */
  const [recentIds, setRecentIds] = useState<string[]>(() => readStoredIds("reputation_recent_companies"));
  const [pinnedIds, setPinnedIds] = useState<string[]>(() => readStoredIds("reputation_pinned_companies"));

  /**
   * A empresa do resumo é a última visitada QUE O USUÁRIO AINDA ACESSA —
   * a lista de recentes é do navegador e sobrevive à perda de vínculo,
   * então cruzar com o perfil evita pedir números de uma empresa que
   * responderia 403.
   */
  // A empresa FIXADA no painel (2026-10-16), ou nulo no modo automático. Mora
  // no navegador, como as recentes; lida no inicializador pelo mesmo motivo.
  const [fixedCompanyId, setFixedCompanyId] = useState<string | null>(() => {
    try {
      return window.localStorage.getItem(HOME_COMPANY_STORAGE_KEY);
    } catch {
      return null;
    }
  });
  const chooseHomeCompany = (companyId: string | null) => {
    setFixedCompanyId(companyId);
    try {
      if (companyId) window.localStorage.setItem(HOME_COMPANY_STORAGE_KEY, companyId);
      else window.localStorage.removeItem(HOME_COMPANY_STORAGE_KEY);
    } catch {
      // ignore
    }
  };
  const fixedCompany = useMemo(
    () => (fixedCompanyId ? activeCompanies(profile.companies).find(company => company.id === fixedCompanyId) ?? null : null),
    [fixedCompanyId, profile.companies]
  );
  const summaryCompany = useMemo(
    () =>
      fixedCompany ??
      recentIds
        .map(id => activeCompanies(profile.companies).find(company => company.id === id))
        .find(company => company !== undefined) ?? null,
    [fixedCompany, recentIds, profile.companies]
  );

  useEffect(() => {
    if (!isRecentOnly || !summaryCompany) {
      setSummary(null);
      return;
    }
    let cancelled = false;
    // A Home não está sob `/empresas/:id`, então a empresa não vem da
    // URL — vai explícita na chamada.
    apiClient.mentionSummary(token, { companyId: summaryCompany.id, days: summaryDays })
      .then(result => { if (!cancelled) setSummary(result); })
      // Falhou: a seção some, em vez de mostrar número errado. É o
      // último lugar da tela que pode ficar em silêncio — as demais
      // reportam erro.
      .catch(() => { if (!cancelled) setSummary(null); });
    return () => { cancelled = true; };
  }, [isRecentOnly, token, summaryCompany, summaryDays]);

  // O feed do hero: as menções mais recentes da MESMA empresa do resumo. Falhou
  // ou veio vazio: o feed some e o hero fica só com o texto — é decoração
  // informativa, nunca motivo de erro na Home.
  useEffect(() => {
    if (!isRecentOnly || !summaryCompany) {
      setRecentMentions([]);
      return;
    }
    let cancelled = false;
    apiClient.listMentions(token, { page: 1, size: HERO_FEED_SIZE }, { companyId: summaryCompany.id })
      .then(page => { if (!cancelled) setRecentMentions(page.items.map(toMention)); })
      .catch(() => { if (!cancelled) setRecentMentions([]); });
    return () => { cancelled = true; };
  }, [isRecentOnly, token, summaryCompany]);

  const togglePin = (companyId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      const nextPinned = pinnedIds.includes(companyId)
        ? pinnedIds.filter(id => id !== companyId)
        : [...pinnedIds, companyId];
      setPinnedIds(nextPinned);
      window.localStorage.setItem("reputation_pinned_companies", JSON.stringify(nextPinned));
    } catch {
      // ignore
    }
  };

  // Só ANOTA a empresa nas recentes: quem leva à empresa é o link do cartão
  // (2026-10-26), que assim vale clique do meio e "abrir em nova aba". O
  // `onClick` do link roda em qualquer clique, inclusive o modificado, e
  // abrir a empresa em outra aba também é acessá-la.
  const rememberRecent = (company: CompanyContext) => {
    try {
      const nextRecent = [company.id, ...recentIds.filter(id => id !== company.id)].slice(0, 6);
      setRecentIds(nextRecent);
      window.localStorage.setItem("reputation_recent_companies", JSON.stringify(nextRecent));
    } catch {
      // ignore
    }
  };

  // Counts for filter pills — já dentro do núcleo escolhido, pra o número
  // da pílula bater com a lista que aparece.
  const counts = useMemo(() => {
    const base = profile.companies.filter(c => matchesGroupFilter(c, effectiveGroupFilter));
    const all = base.length;
    const active = base.filter(c => c.is_active).length;
    const inactive = base.filter(c => !c.is_active).length;
    const admin = base.filter(c => c.permissions.includes("*")).length;
    return { all, active, inactive, admin };
  }, [profile.companies, effectiveGroupFilter]);

  const companies = useMemo(() => {
    // A Home (recentes) não tem o filtro: ele vale só na página de Empresas.
    let list = isRecentOnly
      ? [...profile.companies]
      : profile.companies.filter(company => matchesGroupFilter(company, effectiveGroupFilter));

    // Filter by query and status
    list = list.filter(company => {
      const matchesSearch = company.name.toLocaleLowerCase("pt-BR").includes(query.toLocaleLowerCase("pt-BR"));
      if (!matchesSearch) return false;
      
      const isInactive = !company.is_active;
      if (statusFilter === "active" && isInactive) return false;
      if (statusFilter === "inactive" && !isInactive) return false;
      if (statusFilter === "admin" && !company.permissions.includes("*")) return false;
      
      return true;
    });

    // Sort: Pinned first, then by selected sort option or recent order
    list.sort((a, b) => {
      const isAPinned = pinnedIds.includes(a.id);
      const isBPinned = pinnedIds.includes(b.id);

      if (isAPinned && !isBPinned) return -1;
      if (!isAPinned && isBPinned) return 1;

      // Era bug (consertado em 2026-10-09, teste de render): a Home
      // (`isRecentOnly`) não tem o seletor "Ordenar", então `sortBy`
      // ficava em "name" e a seção "Empresas recentemente acessadas"
      // listava as 6 primeiras do alfabeto. Na Home a ordem é a de
      // recência, independente do seletor.
      if (sortBy === "recent" || isRecentOnly) {
        const indexA = recentIds.indexOf(a.id);
        const indexB = recentIds.indexOf(b.id);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
      }

      return a.name.localeCompare(b.name, "pt-BR");
    });

    if (isRecentOnly) {
      return list.slice(0, 6);
    }

    return list;
  }, [profile.companies, query, statusFilter, sortBy, isRecentOnly, recentIds, pinnedIds, effectiveGroupFilter]);

  const displayedCompanies = useMemo(
    () => companies.slice(0, visibleCount),
    [companies, visibleCount]
  );

  const hasMore = visibleCount < companies.length;

  const confirmToggle = async () => {
    if (!toggling) return;
    setToggleBusy(true);
    setToggleError(null);
    let changed = false;
    try {
      if (toggling.is_active) await apiClient.disableCompany(token, toggling.id);
      else await apiClient.enableCompany(token, toggling.id);
      setToggling(null);
      changed = true;
    } catch (error) {
      setToggleError(
        errorMessage(
          error,
          toggling.is_active
            ? "Não foi possível desativar a empresa."
            : "Não foi possível reativar a empresa."
        )
      );
    } finally {
      setToggleBusy(false);
    }
    if (!changed) return;
    // O `/me` é a fonte do estado: relê, e a lista (e os contadores)
    // se refazem sozinhos. Era bug (consertado em 2026-10-09, teste de
    // render): a releitura ficava no `try` acima, depois de fechar o
    // diálogo, então o erro dela ia pra um diálogo que já não existia e
    // a lista seguia mostrando a empresa como ativa, sem aviso. Agora
    // tem `try` próprio e avisa pelo aviso da página — sem contradizer
    // o servidor, que já fez a troca.
    try {
      await onCompanyStateChanged?.();
    } catch (error) {
      const done = toggling.is_active ? "desativada" : "reativada";
      setNotice(
        `Empresa ${done}, mas a lista não pôde ser atualizada (${errorMessage(error, "falha ao reler os dados")}). Recarregue a página.`
      );
    }
  };

  if (creating) {
    return (
      <CompanyOnboarding
        token={token}
        currentUserName={profile.name}
        companies={profile.companies}
        onCancel={() => setCreating(false)}
        onCreated={onCompanyCreated}
        master={isMaster(profile)}
      />
    );
  }

  return (
    <div className={`company-hub${isRecentOnly ? " company-hub--home" : ""}`}>
      {isRecentOnly && (
        <>
          <HomeBackdrop />
          {/* O hero e os cartões são sempre de UMA empresa — a do resumo.
              Sem ela (ou com a chamada falhando), o hero fica só na
              saudação e a seção dos cartões nem aparece: o `<section>`
              vazio deixava um vão de margem no meio da Home. */}
          <HomeHero
            dayLabel={dayLabel}
            companyCount={profile.companies.length}
            summary={summary}
            company={summaryCompany}
            mentions={recentMentions}
            dashboardHref={summaryCompany ? `/empresas/${summaryCompany.id}/dashboard` : "/empresas"}
            mentionsHref={summaryCompany ? `/empresas/${summaryCompany.id}/mencoes` : "/empresas"}
            companies={activeCompanies(profile.companies)}
            // Só conta como fixada a que ainda existe e está ativa: uma
            // fixada que perdeu o acesso cai no automático sem aviso.
            fixedCompanyId={fixedCompany ? fixedCompany.id : null}
            onChooseCompany={chooseHomeCompany}
            days={summaryDays}
            onDaysChange={setSummaryDays}
          />

          {summary && summaryCompany && (
            <section className="bento-overview" aria-label={`Visão geral de ${summaryCompany.name}`}>
              <DashboardSummaryCards
                summary={summary}
                showsCena={summaryCompany.displays_cena}
              />
            </section>
          )}
        </>
      )}

      <HomePanel enabled={isRecentOnly}>
      <div className={`companies-section-bar${isRecentOnly ? " companies-section-bar--recent" : ""}`}>
        {isRecentOnly ? (
          <div className="companies-title-inline">
            <h2>Empresas recentemente acessadas</h2>
          </div>
        ) : <div />}

        {isRecentOnly && (
          <AppLink className="secondary-button" to="/empresas">
            Ver todas <ArrowRight size={15}/>
          </AppLink>
        )}

        {!isRecentOnly && (
          <div className="companies-actions-inline">
            <div className="search-input-clean">
              <Search size={15} />
              <input
                aria-label="Buscar empresa"
                autoComplete="off"
                value={query}
                onChange={event => {
                  setQuery(event.target.value);
                  setVisibleCount(21);
                }}
                placeholder="Buscar empresa…"
              />
              {query && (
                <button className="clear-btn" onClick={() => setQuery("")} type="button">×</button>
              )}
            </div>

            <div className="status-filter-pills">
              <button
                type="button"
                className={`status-pill-btn ${statusFilter === "all" ? "active" : ""}`}
                onClick={() => { setStatusFilter("all"); setVisibleCount(21); }}
              >
                Todas {statusFilter === "all" && `(${counts.all})`}
              </button>
              <button
                type="button"
                className={`status-pill-btn ${statusFilter === "active" ? "active" : ""}`}
                onClick={() => { setStatusFilter("active"); setVisibleCount(21); }}
              >
                Ativas {statusFilter === "active" && `(${counts.active})`}
              </button>
              <button
                type="button"
                className={`status-pill-btn ${statusFilter === "inactive" ? "active" : ""}`}
                onClick={() => { setStatusFilter("inactive"); setVisibleCount(21); }}
              >
                Inativas {statusFilter === "inactive" && `(${counts.inactive})`}
              </button>
            </div>

            {canFilterByGroup && (
              <div className="sort-select-wrapper">
                <span className="sort-label">Núcleo:</span>
                <Select
                  className="sort-select"
                  value={effectiveGroupFilter}
                  onChange={e => { setGroupFilter(e.target.value); setVisibleCount(21); }}
                  aria-label="Filtrar empresas por núcleo"
                >
                  <option value="all">Todos</option>
                  {groupOptions.map(group => (
                    <option key={group.id} value={group.id}>{group.name}</option>
                  ))}
                  <option value={NO_GROUP}>Sem núcleo</option>
                </Select>
              </div>
            )}

            <div className="sort-select-wrapper">
              <span className="sort-label">Ordenar:</span>
              <Select
                className="sort-select"
                value={sortBy}
                onChange={e => setSortBy(e.target.value as "name" | "recent")}
                aria-label="Ordenar empresas"
              >
                <option value="name">A-Z (Nome)</option>
                <option value="recent">Recentes</option>
              </Select>
            </div>

            {isAdministrator(profile) && (
              <button className="primary-button create-company-btn" type="button" onClick={() => setCreating(true)}>
                <Plus size={15} /> Nova empresa
              </button>
            )}
          </div>
        )}
      </div>

      <section className="company-list" aria-label="Empresas disponíveis">
        {displayedCompanies.map(company => {
          const isPinned = pinnedIds.includes(company.id);
          const isInactive = !company.is_active;
          return (
            <div className={`company-card ${isPinned ? "is-pinned" : ""} ${isInactive ? "is-inactive" : ""}`} key={company.id}>
              {/* O card NÃO é um `<button>`, e o botão que o abre é uma
                  camada esticada por cima. Era um botão contendo o botão
                  de fixar — HTML inválido, que o React acusa como erro de
                  hidratação e cujo efeito prático é os dois cliques
                  competirem, com o desfecho dependendo do navegador.
                  Aninhar interativo em interativo não tem conserto por
                  `stopPropagation`; o que resolve é não aninhar. */}
              <AppLink
                className="company-card-open"
                to={`/empresas/${company.id}/visao-geral`}
                onClick={() => rememberRecent(company)}
                aria-label={`Abrir ${company.name}`}
              />
              <div className="company-card-top-right">
                {isInactive ? (
                  <span className="status-pill status-pill-inactive"><i/>Inativa</span>
                ) : (
                  <span className="status-pill"><i/>Ativa</span>
                )}
                {/* Desativar/reativar é um ícone ao lado do pino (era um
                    botão escrito no pé do cartão). Só o Máster vê. */}
                {canToggleCompanies && (
                  <button
                    type="button"
                    className="company-card-toggle"
                    title={company.is_active ? "Desativar empresa" : "Reativar empresa"}
                    onClick={() => { setToggleError(null); setToggling(company); }}
                    aria-label={`${company.is_active ? "Desativar" : "Reativar"} ${company.name}`}
                  >
                    {company.is_active ? <Power size={14} /> : <RotateCcw size={14} />}
                  </button>
                )}
                <button
                  type="button"
                  className={`pin-company-btn ${isPinned ? "pinned" : ""}`}
                  title={isPinned ? "Desafixar empresa" : "Fixar empresa no topo"}
                  aria-label={isPinned ? "Desafixar empresa" : "Fixar empresa no topo"}
                  onClick={e => togglePin(company.id, e)}
                >
                  <Pin size={14} />
                </button>
              </div>

              <div className={`company-card-mark ${company.logo_url ? "has-logo" : ""}`}>
                {company.logo_url
                  ? <img src={resolveApiAssetUrl(company.logo_url)} alt=""/>
                  : company.name.slice(0, 2).toUpperCase()}
              </div>
              <div className="company-card-copy">
                <strong>{company.name}</strong>
                <p>{company.permissions.includes("*") ? "Acesso administrativo" : `${company.permissions.length} permissões concedidas`}</p>
              </div>
            </div>
          );
        })}
        {companies.length === 0 && (
          <div className="company-empty">
            <Search size={28}/><strong>Nenhuma empresa encontrada</strong>
            <span>
              {canFilterByGroup
                ? "Revise o termo pesquisado ou troque os filtros de status e núcleo."
                : "Revise o termo pesquisado ou troque o filtro de status."}
            </span>
          </div>
        )}
      </section>
      </HomePanel>

      {hasMore && (
        <div className="load-more-wrapper">
          <button
            type="button"
            className="load-more-btn"
            onClick={() => setVisibleCount(prev => prev + 21)}
          >
            <span>Carregar mais</span>
            <span className="remaining-count">+{companies.length - visibleCount}</span>
            <ChevronDown size={15} />
          </button>
        </div>
      )}

      {toggling && (
        <ConfirmDialog
          title={toggling.is_active ? `Desativar ${toggling.name}?` : `Reativar ${toggling.name}?`}
          description={
            toggling.is_active
              ? "Desativar esconde a empresa de todo mundo que não é Máster e bloqueia o envio de upload. Nada é apagado: menções, amostras e cadastro continuam como estão, e você pode reativar quando quiser."
              : "A empresa volta a aparecer para quem tem acesso a ela e volta a aceitar upload. Nada do que já existe é alterado."
          }
          confirmLabel={toggling.is_active ? "Desativar" : "Reativar"}
          danger={toggling.is_active}
          loading={toggleBusy}
          error={toggleError}
          onConfirm={() => void confirmToggle()}
          onCancel={() => setToggling(null)}
        />
      )}

      {notice && <Toast message={notice} onClose={() => setNotice(null)} />}

      {editingCompany && (
        <CompanyEditModal
          token={token}
          company={editingCompany}
          onClose={() => setEditingCompany(null)}
          onUpdated={async (updated) => {
            await onCompanyCreated(updated);
            setEditingCompany(null);
          }}
        />
      )}

      {/* Na Home o atalho não entra: a Administração tem item próprio no
          menu lateral. Na página de Empresas ele continua. */}
      {isMaster(profile) && !isRecentOnly && (
        <section className="admin-shortcuts">
          <div className="section-heading">
            <div><span className="eyebrow">ADMINISTRAÇÃO</span><h2>Área administrativa</h2></div>
            <p>Usuários, modelos de IA, regras do CENA, núcleos e auditoria entre empresas.</p>
          </div>
          <AppLink className="admin-shortcut-live" to={adminHref}>
            <ShieldCheck size={19}/>
            <div><strong>Ir para Administração</strong><span>Gestão entre empresas</span></div>
            <ArrowRight size={16}/>
          </AppLink>
        </section>
      )}
    </div>
  );
}
